import type { KeyboardDragState } from './types';
import { clampIndexByListLength, isFiniteNumber } from './untils';

/**
 * Creates an idle keyboard drag state.
 */
export function createKeyboardDragState(): KeyboardDragState {
  return {
    mode: 'idle',
    activeIndex: null,
    originIndex: null,
    currentIndex: null,
  };
}

/**
 * Starts keyboard drag mode from the provided item index.
 */
export function pickKeyboardDragItem({
  state,
  index,
  listLength,
}: {
  state: KeyboardDragState;
  index: number;
  listLength: number;
}): KeyboardDragState {
  if (!isFiniteNumber(index) || !isFiniteNumber(listLength)) return state;
  if (listLength < 2) return state;
  if (state.mode === 'dragging') return state;

  const clampedIndex = clampIndexByListLength({
    value: index,
    listLength,
  });

  return {
    mode: 'dragging',
    activeIndex: clampedIndex,
    originIndex: clampedIndex,
    currentIndex: clampedIndex,
  };
}

/**
 * Moves the active keyboard drag target by one step.
 */
export function moveKeyboardDragItem({
  state,
  direction,
  listLength,
}: {
  state: KeyboardDragState;
  direction: 'up' | 'down';
  listLength: number;
}): KeyboardDragState {
  if (!isFiniteNumber(listLength)) return state;
  if (state.mode !== 'dragging') return state;

  const currentIndex = state.currentIndex;
  if (currentIndex === null) return state;

  const delta = direction === 'up' ? -1 : 1;
  const nextIndex = clampIndexByListLength({
    value: currentIndex + delta,
    listLength,
  });

  if (nextIndex === currentIndex) {
    return state;
  }

  return {
    ...state,
    currentIndex: nextIndex,
    activeIndex: nextIndex,
  };
}

/**
 * Cancels keyboard drag and returns idle state.
 */
export function cancelKeyboardDragItem(): KeyboardDragState {
  return createKeyboardDragState();
}

/**
 * Completes keyboard drag and returns reorder metadata for consumers.
 */
export function dropKeyboardDragItem({ state }: { state: KeyboardDragState }): {
  nextState: KeyboardDragState;
  didReorder: boolean;
  fromIndex: number | null;
  toIndex: number | null;
} {
  if (state.mode !== 'dragging') {
    return {
      nextState: state,
      didReorder: false,
      fromIndex: null,
      toIndex: null,
    };
  }

  const fromIndex = state.originIndex;
  const toIndex = state.currentIndex;

  if (fromIndex === null || toIndex === null) {
    return {
      nextState: createKeyboardDragState(),
      didReorder: false,
      fromIndex,
      toIndex,
    };
  }

  return {
    nextState: createKeyboardDragState(),
    didReorder: fromIndex !== toIndex,
    fromIndex,
    toIndex,
  };
}
